import { create } from 'zustand';
import { organizationApi, programApi, releaseTrainApi } from '../services/api';
import type { Organization, Program, ReleaseTrain } from '../types';

interface OrgHierarchyState {
  organizations: Organization[];
  programs: Program[];
  releaseTrains: ReleaseTrain[];
  selectedOrgId: string | null;
  loading: boolean;
  error: string | null;

  fetchOrganizations: () => Promise<void>;
  fetchPrograms: (orgId?: string) => Promise<void>;
  fetchReleaseTrains: (programId?: string) => Promise<void>;
  fetchOrgTree: (orgId: string) => Promise<Organization | null>;
  setSelectedOrg: (orgId: string | null) => void;

  createOrganization: (data: Partial<Organization>) => Promise<Organization>;
  updateOrganization: (id: string, data: Partial<Organization>) => Promise<Organization>;
  deleteOrganization: (id: string) => Promise<void>;

  createProgram: (data: Partial<Program>) => Promise<Program>;
  updateProgram: (id: string, data: Partial<Program>) => Promise<Program>;
  deleteProgram: (id: string) => Promise<void>;

  createReleaseTrain: (data: Partial<ReleaseTrain>) => Promise<ReleaseTrain>;
  updateReleaseTrain: (id: string, data: Partial<ReleaseTrain>) => Promise<ReleaseTrain>;
  deleteReleaseTrain: (id: string) => Promise<void>;
}

export const useOrgHierarchyStore = create<OrgHierarchyState>((set) => ({
  organizations: [],
  programs: [],
  releaseTrains: [],
  selectedOrgId: null,
  loading: false,
  error: null,

  setSelectedOrg: (orgId) => set({ selectedOrgId: orgId }),

  fetchOrganizations: async () => {
    set({ loading: true, error: null });
    try {
      const orgs = await organizationApi.list();
      set({ organizations: orgs, loading: false });
    } catch (err: any) {
      set({ error: err.message, loading: false });
    }
  },

  fetchPrograms: async (orgId) => {
    try {
      const programs = await programApi.list(orgId);
      set({ programs });
    } catch (err: any) {
      set({ error: err.message });
    }
  },

  fetchReleaseTrains: async (programId) => {
    try {
      const trains = await releaseTrainApi.list(programId);
      set({ releaseTrains: trains });
    } catch (err: any) {
      set({ error: err.message });
    }
  },

  fetchOrgTree: async (orgId) => {
    try {
      const tree = await organizationApi.getTree(orgId);
      return tree;
    } catch {
      return null;
    }
  },

  createOrganization: async (data) => {
    const org = await organizationApi.create(data);
    set((s) => ({ organizations: [...s.organizations, org] }));
    return org;
  },

  updateOrganization: async (id, data) => {
    const org = await organizationApi.update(id, data);
    set((s) => ({
      organizations: s.organizations.map((o) => (o._id === id ? org : o)),
    }));
    return org;
  },

  deleteOrganization: async (id) => {
    await organizationApi.delete(id);
    set((s) => ({
      organizations: s.organizations.filter((o) => o._id !== id),
    }));
  },

  createProgram: async (data) => {
    const program = await programApi.create(data);
    set((s) => ({ programs: [...s.programs, program] }));
    return program;
  },

  updateProgram: async (id, data) => {
    const program = await programApi.update(id, data);
    set((s) => ({
      programs: s.programs.map((p) => (p._id === id ? program : p)),
    }));
    return program;
  },

  deleteProgram: async (id) => {
    await programApi.delete(id);
    set((s) => ({
      programs: s.programs.filter((p) => p._id !== id),
    }));
  },

  createReleaseTrain: async (data) => {
    const train = await releaseTrainApi.create(data);
    set((s) => ({ releaseTrains: [...s.releaseTrains, train] }));
    return train;
  },

  updateReleaseTrain: async (id, data) => {
    const train = await releaseTrainApi.update(id, data);
    set((s) => ({
      releaseTrains: s.releaseTrains.map((t) => (t._id === id ? train : t)),
    }));
    return train;
  },

  deleteReleaseTrain: async (id) => {
    await releaseTrainApi.delete(id);
    set((s) => ({
      releaseTrains: s.releaseTrains.filter((t) => t._id !== id),
    }));
  },
}));
